import { uid } from '@runtime/shared/utils/id'
import { createJsonStorageRepo } from '@runtime/shared/utils/storage'
import type { UserRole } from '@runtime/core/user/userRole'

// ===== PLAYER PROFILE =====

export type UserProfile = {
  id: string
  createdAt: number
  pseudo?: string
  email?: string
  role?: UserRole
  avatarDataUrl?: string | null
  rating?: number
  level?: string
  games?: UserGamesSummary
  history?: GameHistoryEntry[]
  favorites?: string[]
}

export type GameHistoryEntry = {
  id: string
  ts: number
  kind?: string
  grid?: number
  vsAI?: boolean
  myName?: string
  opponents?: string[]
  result?: 'win' | 'loss' | 'draw'
  scoreFor?: number | null
  scoreAgainst?: number | null
  durationMs?: number
}

export type UserGamesSummary = {
  total?: number
  vsAI?: { played: number; wins: number; losses: number; draws: number }
  vsPlayers?: { played: number; wins: number; losses: number; draws: number }
  puzzle?: { played: number; completed: number }
}

export type FullGameRecord = {
  id: string
  timestamp?: number
} & Record<string, unknown>

type BoardCellInput = { key: string; rot?: number } | null

// === Full games (final board + moves)
// Stored separately from the main store to keep it from growing too much.
const FULL_GAME_KEY = 'us_full_games_v1'
const fullGamesRepo = createJsonStorageRepo<Record<string, FullGameRecord>>(
  FULL_GAME_KEY,
  {},
)

export function snapshotBoard(board: Array<Array<BoardCellInput>> | null) {
  if (!board) return null
  return board.map((row) =>
    row.map((cell) => (cell ? { key: cell.key, rot: cell.rot || 0 } : null)),
  )
}

export function saveFullGame(full: FullGameRecord) {
  if (!full || !full.id) return
  const map = fullGamesRepo.read()
  map[full.id] = full

  // Keep the latest 50 to avoid bloating localStorage.
  const entries = Object.entries(map)
    .sort((a, b) => (b[1].timestamp || 0) - (a[1].timestamp || 0))
    .slice(0, 50)

  const trimmed: Record<string, FullGameRecord> = {}
  for (const [id, game] of entries) {
    trimmed[id] = game
  }
  fullGamesRepo.write(trimmed)
}

export function loadFullGame(id: string) {
  return fullGamesRepo.read()[id] || null
}

export function loadAllFullGames() {
  return fullGamesRepo.read()
}

export function computeLevelFromRating(rating?: number) {
  const r = rating || 1200
  if (r >= 1650) return 'expert'
  if (r >= 1450) return 'strong'
  if (r >= 1250) return 'intermediate'
  return 'beginner'
}

// Ranking tiers (can be adjusted).
export const ELO_TIERS = [
  { id: 'bronze', min: 800, max: 1099 },
  { id: 'silver', min: 1100, max: 1299 },
  { id: 'gold', min: 1300, max: 1499 },
  { id: 'plat', min: 1500, max: 1699 },
  { id: 'diamond', min: 1700, max: 1899 },
  { id: 'master', min: 1900, max: 2200 },
]

export function tierForRating(r?: number) {
  const rating = r || 1200
  const t = ELO_TIERS.find((t) => rating >= t.min && rating <= t.max)
  return t || ELO_TIERS[0]
}

export function makeDefaultUser(): UserProfile {
  return {
    id: uid(),
    createdAt: Date.now(),
    rating: 1200,
    level: 'beginner',
    games: {
      total: 0,
      vsAI: { played: 0, wins: 0, losses: 0, draws: 0 },
      vsPlayers: { played: 0, wins: 0, losses: 0, draws: 0 },
      puzzle: { played: 0, completed: 0 },
    },
    history: [],
    favorites: [],
  }
}
